import type { PlayerItemResponse } from '@on3/api';
import { parseJerseySortValue } from '@on3/ui-lib/utils/formatters';
import {
  getStatusType,
  StatusTypeValue,
} from '@public/components/College/Interests/Commits/CommitList';
import {
  ColumnHeaders,
  type ColumnOption,
} from '@public/components/Db/Filters/SortFilter';
import { IndustryComparisonColumnHeaderAlt } from '@public/components/Db/Rankings/IndustryComparison/IndustryComparisonColumnHeaderAlt';
import { IndustryComparisonItem } from '@public/components/Db/Rankings/IndustryComparison/IndustryComparisonItem';
import { useCallback, useState } from 'react';

import styles from './IndustryComparisonListAlt.module.scss';

interface IndustryComparisonListAltProps {
  /**List of players using PlayerItemResponse Model*/
  playerList?: PlayerItemResponse[] | null;
  columns?: ColumnOption[];
}

const useSortList = (list?: PlayerItemResponse[] | null) =>
  useCallback(
    (sortBy: ColumnOption, ascending: boolean) => {
      if (!list?.length) {
        return list;
      }

      const isRank = sortBy === 'rank';
      const isJerseyNumber = sortBy === 'jerseyNumber';
      const isPlayer = sortBy === 'lastName';
      const isStatus = sortBy === 'status';
      const isStatusDate = sortBy === 'date';
      const sortedList = [...list]?.sort((a, b) => {
        const setIndex =
          a.industryComparison?.findIndex((i) => i.type === sortBy) || 0;

        let sortDirection = ascending;
        let setA = null;
        let setB = null;
        let secondarySortA = null;
        let secondarySortB = null;

        if (isStatus) {
          const statusA = a.status?.transferredAsset
            ? 'Transferred'
            : a.status?.type;
          const statusB = b.status?.transferredAsset
            ? 'Transferred'
            : b.status?.type;

          const statusTypeA = getStatusType(statusA);
          const statusTypeB = getStatusType(statusB);

          const statusValueA = StatusTypeValue[statusTypeA];
          const statusValueB = StatusTypeValue[statusTypeB];

          setA = statusValueA;
          setB = statusValueB;
        } else if (isRank) {
          setA = a?.industryComparison?.[0].overallRank;
          setB = b?.industryComparison?.[0].overallRank;
        } else if (isJerseyNumber) {
          setA = parseJerseySortValue(a.player?.[sortBy]);
          setB = parseJerseySortValue(b.player?.[sortBy]);
        } else if (isPlayer) {
          setA = a.player?.[sortBy];
          setB = b.player?.[sortBy];
        } else {
          const setAobject = a.player && a.industryComparison?.[setIndex];
          const setBobject = b.player && b.industryComparison?.[setIndex];

          setA = setAobject?.rating;
          setB = setBobject?.rating;
          secondarySortA = setAobject?.overallRank;
          secondarySortB = setBobject?.overallRank;
        }

        if (isStatusDate) {
          setA = a?.status?.date;
          setB = b?.status?.date;
        }

        // secondary sort by rank if no rating exists
        if (
          ((!setA && !setB) || setA === setB) &&
          (secondarySortA || secondarySortB)
        ) {
          // flip direction due to rating and rank being inverses of each other
          sortDirection = !ascending;
          setA = secondarySortA;
          setB = secondarySortB;
        }

        //  puts nulls at the end of list
        if (!setA && setA !== 0) {
          return 1;
        }

        if (!setB && setB !== 0) {
          return -1;
        }

        if (setA < setB) {
          return sortDirection ? -1 : 1;
        }

        if (setA > setB) {
          return sortDirection ? 1 : -1;
        }

        return 0;
      });

      return sortedList.sort((a, b) => {
        const statusA = a.status?.transfer ? 1 : -1;
        const statusB = b.status?.transfer ? 1 : -1;

        return statusA - statusB;
      });
    },
    [list],
  );

export const IndustryComparisonListAlt = ({
  playerList,
  columns = ['lastName', 'Industry', 'On3', '247', 'Espn', 'Rivals', 'date'],
}: IndustryComparisonListAltProps) => {
  const sortList = useSortList(playerList);
  const [selected, setSelected] = useState<ColumnOption>('Industry');

  const [list, setList] = useState(sortList('Industry', false));

  const displaySelectedObject = ColumnHeaders.find(
    (item) => item.name === selected,
  );
  const [selectedAria, setSelectedAria] = useState(
    displaySelectedObject?.ariaLabel,
  );

  const [ascending, setAscending] = useState(false);

  const [disabled, setDisabled] = useState(false);

  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      setDisabled(true);
      const { dataset } = e.currentTarget;

      const { sorttype = '', flip = 'false' } = dataset;

      const isFlipped = flip === 'true';
      let orderBy = ascending;

      setSelectedAria(e.currentTarget.id);
      if (sorttype !== selected) {
        setSelected(sorttype as ColumnOption);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttype as ColumnOption, orderBy);

      sortedList && setList(sortedList);
      setDisabled(false);
    },
    [ascending, selected, sortList],
  );

  return (
    <div className={styles.block}>
      {list?.length ? (
        <>
          <IndustryComparisonColumnHeaderAlt
            ascending={ascending}
            columns={columns}
            disabled={disabled}
            onChange={handleChange}
            selected={selected}
          />
          <div
            aria-label="Sortable Industry Comparison player list"
            aria-labelledby={selectedAria}
            role="table"
          >
            <ol
              className={styles.list}
              data-sort-type={selected}
              id="player-list"
              role="rowgroup"
            >
              {list.map((item) => {
                const { player, industryComparison, status, nilValue, rpm } =
                  item;

                if (!player) return null;

                const ratings =
                  industryComparison?.filter((i) => i.type !== 'Consensus') ||
                  [];

                return (
                  <IndustryComparisonItem
                    columns={columns}
                    key={player.key}
                    nilValue={nilValue?.totalValue}
                    player={player}
                    predictions={rpm}
                    ratings={ratings}
                    selected={selected}
                    status={status}
                  />
                );
              })}
            </ol>
          </div>
        </>
      ) : (
        <p className={styles.noListText}>
          There are no players for this selection.
        </p>
      )}
    </div>
  );
};
